/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  GeoWebPlugin,
  GeoWebPluginInstance,
} from '@opengeoweb/plugin-interface';
import { loadPlugin, PluginManager } from '@opengeoweb/plugin-interface';
import { getBundleUrl, usePluginCatalog } from '../api';
import type { PluginRegistry } from '../api';
import { usePluginLiveReload } from './usePluginLiveReload';

export type PluginLoader = (url: string) => Promise<{ plugin: GeoWebPlugin }>;

interface PluginContextType {
  loadedPlugins: Record<string, GeoWebPlugin>;
  pluginInstances: Record<string, GeoWebPluginInstance>;
  loadPlugin: (pluginId: string, version?: string) => Promise<void>;
  unloadPlugin: (pluginId: string) => void;
  createPluginBridge: () => PluginManager;
  registerInstance: (
    instanceId: string,
    instance: GeoWebPluginInstance,
  ) => void;
  unregisterInstance: (instanceId: string) => void;
  getPluginInstance: (instanceId: string) => GeoWebPluginInstance | undefined;
  pluginCatalog: PluginRegistry | undefined;
  failedPlugins: Record<string, string>;
}

const PluginContext = createContext<PluginContextType | undefined>(undefined);

export const PluginProvider: React.FC<{
  children: React.ReactNode;
  pluginLoader?: PluginLoader;
}> = ({ children, pluginLoader }) => {
  const [loadedPlugins, setLoadedPlugins] = useState<
    Record<string, GeoWebPlugin>
  >({});
  const loadedPluginsRef = useRef(loadedPlugins);
  loadedPluginsRef.current = loadedPlugins;

  const [failedPlugins, setFailedPlugins] = useState<Record<string, string>>(
    {},
  );
  const {
    isAlreadyLoaded,
    setLoadedPluginUrl,
    loadedPluginRequests,
    registerRequest,
    unregisterRequest,
  } = usePluginLiveReload();

  const [pluginInstances, setPluginInstances] = useState<
    Record<string, GeoWebPluginInstance>
  >({});
  const { data: pluginCatalog } = usePluginCatalog();

  const loadPluginById = useCallback(
    async (id: string, version?: string) => {
      const pluginKey = version ? `${id}@${version}` : id;
      registerRequest(pluginKey, id, version);

      if (!pluginCatalog) {
        return;
      }

      const pluginMeta = pluginCatalog[id];
      if (!pluginMeta) {
        // If the plugin is already loaded, treat a catalog miss as transient (e.g. dev rebuild) and keep the current version until the next refetch.
        if (!loadedPluginsRef.current[pluginKey]) {
          setFailedPlugins((prev) => ({
            ...prev,
            [pluginKey]: `Plugin with ID ${id} not found in catalog`,
          }));
        }
        return;
      }

      let { url } = pluginMeta;

      if (version && pluginMeta.versions?.length) {
        // Plugin has multiple versions - require exact match
        if (pluginMeta.versions.includes(version)) {
          url = getBundleUrl(pluginMeta.name, version);
        } else {
          console.warn(`Version ${version} not found for plugin ${id}`);
          return;
        }
      } else if (
        version &&
        pluginMeta.version &&
        version !== pluginMeta.version
      ) {
        // Single-version plugin but requested version doesn't match
        console.warn(`Version ${version} not found for plugin ${id}`);
        return;
      }

      if (
        loadedPluginsRef.current[pluginKey] &&
        isAlreadyLoaded(pluginKey, url)
      ) {
        return;
      }

      try {
        const plugin = await loadPlugin(url, pluginLoader);
        setLoadedPlugins((prev) => ({ ...prev, [pluginKey]: plugin }));
        setLoadedPluginUrl(pluginKey, url);
        setFailedPlugins((prev) => {
          if (!(pluginKey in prev)) {
            return prev;
          }
          const updatedFailures = { ...prev };
          delete updatedFailures[pluginKey];
          return updatedFailures;
        });
      } catch (err) {
        console.error(
          `Failed to load plugin ${id}@${version || 'latest'}:`,
          err,
        );
        setFailedPlugins((prev) => ({
          ...prev,
          [pluginKey]: err instanceof Error ? err.message : String(err),
        }));
      }
    },
    [
      isAlreadyLoaded,
      pluginCatalog,
      pluginLoader,
      registerRequest,
      setLoadedPluginUrl,
    ],
  );

  useEffect(() => {
    if (!pluginCatalog) {
      return;
    }

    const requests = Object.values(loadedPluginRequests);
    if (requests.length === 0) {
      return;
    }

    requests.forEach((request) => {
      void loadPluginById(request.id, request.version);
    });
  }, [pluginCatalog, loadedPluginRequests, loadPluginById]);

  const unloadPlugin = useCallback(
    (pluginId: string) => {
      setLoadedPlugins((prev) => {
        const updatedPlugins = { ...prev };
        delete updatedPlugins[pluginId];
        return updatedPlugins;
      });
      unregisterRequest(pluginId);
    },
    [unregisterRequest],
  );

  const registerInstance = useCallback(
    (instanceId: string, instance: GeoWebPluginInstance) => {
      setPluginInstances((prev) => ({ ...prev, [instanceId]: instance }));
    },
    [],
  );

  const unregisterInstance = useCallback((instanceId: string) => {
    setPluginInstances((prev) => {
      const updatedInstances = { ...prev };
      delete updatedInstances[instanceId];
      return updatedInstances;
    });
  }, []);

  const getPluginInstance = useCallback(
    (instanceId: string) => pluginInstances[instanceId],
    [pluginInstances],
  );

  // Factory function - creates a new PluginManager each time it's called
  const createPluginBridge = useCallback(() => new PluginManager(), []);

  const value = useMemo<PluginContextType>(
    () => ({
      loadedPlugins,
      failedPlugins,
      pluginCatalog,
      pluginInstances,
      loadPlugin: loadPluginById,
      unloadPlugin,
      registerInstance,
      unregisterInstance,
      getPluginInstance,
      createPluginBridge,
    }),
    [
      loadedPlugins,
      failedPlugins,
      pluginCatalog,
      pluginInstances,
      loadPluginById,
      unloadPlugin,
      registerInstance,
      unregisterInstance,
      getPluginInstance,
      createPluginBridge,
    ],
  );

  return (
    <PluginContext.Provider value={value}>{children}</PluginContext.Provider>
  );
};

export const usePlugin = (): PluginContextType => {
  const context = useContext(PluginContext);
  if (!context) {
    throw new Error('usePlugin must be used within a PluginProvider');
  }
  return context;
};
